import { Badge, Button, Card, CardContent, LoadingState } from '@sitewise/ui';
import { useQuery } from '@tanstack/react-query';
import { ClipboardCheck } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

import { listDevices } from '../../../api/compliance.js';

import type { UnitRow } from '../../../api/buildings.js';
import type { UnitDetailBuilding } from '../../../api/units.js';

interface Props {
  unit: UnitRow;
  building: UnitDetailBuilding;
}

/**
 * Devices that live in this unit, grouped by tracked vs inventory.
 * Mirrors the building-level Compliance tab; "Add device" deep-links
 * to the compliance/devices page with the unit pre-filtered so the
 * existing add-device flow can take over.
 */
export function ComplianceTab({ unit, building: _building }: Props) {
  const navigate = useNavigate();

  const devicesQuery = useQuery({
    queryKey: ['devices', { unitId: unit.id }],
    queryFn: () => listDevices({ unitId: unit.id, limit: 100 }),
  });

  const devices = devicesQuery.data?.devices ?? [];
  const tracked = devices.filter((d) => deviceIsTracked(d));
  const inventory = devices.filter((d) => !deviceIsTracked(d));

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Link
          to={`/compliance/devices?unitId=${unit.id}`}
          className="text-sm text-primary hover:underline"
        >
          View all devices for this unit →
        </Link>
        <Button size="sm" onClick={() => navigate(`/compliance/devices?unitId=${unit.id}`)}>
          <ClipboardCheck className="h-3.5 w-3.5" /> Add device
        </Button>
      </div>
      {devicesQuery.isLoading ? (
        <LoadingState rows={3} />
      ) : devices.length === 0 ? (
        <Card>
          <CardContent className="p-6 text-sm text-fg-secondary">
            No devices recorded for this unit yet.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {tracked.length > 0 ? (
            <Section title={`Compliance-tracked (${tracked.length})`}>
              {tracked.map((d) => (
                <DeviceRow
                  key={d.id}
                  device={d}
                  onClick={() => navigate(`/compliance/devices?focus=${d.id}`)}
                />
              ))}
            </Section>
          ) : null}
          {inventory.length > 0 ? (
            <Section title={`Inventory (${inventory.length})`}>
              {inventory.map((d) => (
                <DeviceRow
                  key={d.id}
                  device={d}
                  onClick={() => navigate(`/compliance/devices?focus=${d.id}`)}
                />
              ))}
            </Section>
          ) : null}
        </div>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Card>
      <CardContent className="space-y-1 p-0">
        <div className="border-b border-border px-5 py-2 text-xs font-semibold uppercase tracking-wide text-fg-muted">
          {title}
        </div>
        <div className="divide-y divide-border">{children}</div>
      </CardContent>
    </Card>
  );
}

interface DeviceRowProps {
  device: {
    id: string;
    label: string;
    deviceType: { name: string };
    nextDueAt: string | null;
    isActive: boolean;
  };
  onClick: () => void;
}

function DeviceRow({ device, onClick }: DeviceRowProps) {
  return (
    <button
      onClick={onClick}
      className="flex w-full items-center justify-between gap-3 px-5 py-3 text-left text-sm hover:bg-surface-muted"
    >
      <div className="min-w-0">
        <div className="truncate font-medium">{device.label}</div>
        <div className="truncate text-xs text-fg-muted">{device.deviceType.name}</div>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {device.nextDueAt ? (
          <span className="text-xs text-fg-muted tabular-nums">
            Due {new Date(device.nextDueAt).toLocaleDateString()}
          </span>
        ) : null}
        {!device.isActive ? <Badge variant="outline">Inactive</Badge> : null}
      </div>
    </button>
  );
}

function deviceIsTracked(d: {
  isComplianceTrackedOverride: boolean | null;
  deviceType: { isComplianceTracked: boolean };
}): boolean {
  return d.isComplianceTrackedOverride ?? d.deviceType.isComplianceTracked;
}
